import type { NotificationObject } from "@/composables/common/useGlobalActions"
import type { I18nString } from "@/lang/config"
import type {
	AppNotification,
	AppNotificationAction,
	AppNotificationCategory,
	AppNotificationType,
	BroadcastNotification
} from "@/types/notifications"
import { NButton } from "naive-ui"
import { h } from "vue"
import { useNavigation } from "@/composables/router/useNavigation"
import { emitter } from "@/emitter"
import { i18nGlobal } from "@/lang"
import { BroadcastNotificationType } from "@/types/notifications"
import { dFormat } from "@/utils/dayjs"

export function broadcastToAppNotification(item: BroadcastNotification): AppNotification {
	const { t } = i18nGlobal
	const { routeProfile, routeExam } = useNavigation()

	let category: AppNotificationCategory = "alert"
	let type: AppNotificationType = "default"
	let title = t<I18nString>(`common.misc.alert`)
	let action: AppNotificationAction | undefined

	switch (item.type) {
		case BroadcastNotificationType.PATIENT_INVITES_DOCTOR:
			category = "link"
			type = "success"
			title = t<I18nString>(`notifications.types.connection_request`)
			action = {
				callback: () => {
					emitter.emit("open:invitation-request", { patient_id: item.meta.patient_id })
				},
				label: t<I18nString>(`common.misc.open`)
			}
			break
		case BroadcastNotificationType.NEW_REPORTS_ADD_TO_EXAM:
			category = "report"
			type = "warning"
			title = t<I18nString>(`notifications.types.new_report_uploaded`)
			action = {
				callback: () => {
					routeExam({
						patientId: item.meta.patient_id,
						patientName: item.meta.patient_name,
						examId: item.meta.exam_id
					}).navigate()
				},
				label: t<I18nString>(`common.misc.open`)
			}
			break
		case BroadcastNotificationType.ADMIN_DOCUMENT_ACCEPTED:
			category = "report"
			type = "success"
			title = "Documento di riconoscimento accettato"
			action = {
				callback: () => {
					routeProfile({ activeTab: "security" }).navigate()
				},
				label: t<I18nString>(`common.misc.open`)
			}
			break
		case BroadcastNotificationType.ADMIN_DOCUMENT_REFUSED:
			category = "report"
			type = "warning"
			title = "Documento di riconoscimento rifiutato"
			action = {
				callback: () => {
					routeProfile({ activeTab: "security" }).navigate()
				},
				label: t<I18nString>(`common.misc.open`)
			}
			break
	}

	return {
		id: item.id,
		category,
		type,
		title,
		description: item.text,
		read: item.read,
		date: dFormat(item.created_at, "datetime"),
		action
	}
}

export function broadcastToNotificationObject(item: BroadcastNotification): NotificationObject {
	const { t } = i18nGlobal

	const appNotification = broadcastToAppNotification(item)

	const notify: NotificationObject = {
		title: appNotification.title,
		content: appNotification.description,
		type: appNotification.type,
		meta: appNotification.date,
		duration: 3_500,
		keepAliveOnHover: true
	}

	if (appNotification.action) {
		notify.action = () =>
			h(
				NButton,
				{
					text: true,
					type: appNotification.type,
					onClick: appNotification.action?.callback
				},
				{
					default: () => appNotification.action?.label || t<I18nString>(`common.misc.details`)
				}
			)
	}

	return notify
}
